import { faFacebookF } from '@fortawesome/free-brands-svg-icons/faFacebookF';
import { faLinkedinIn } from '@fortawesome/free-brands-svg-icons/faLinkedinIn';
import { faXTwitter } from '@fortawesome/free-brands-svg-icons/faXTwitter';
import { faEnvelope } from '@fortawesome/pro-solid-svg-icons/faEnvelope';
import { faLink } from '@fortawesome/pro-solid-svg-icons/faLink';
import { trackGaEvent } from '@propertypal/shared/src/services/analytics';
import platform from '@propertypal/shared/src/utils/platform';
import { useRouter } from 'next/router';
import React, { useMemo } from 'react';
import ContentHubNewsLetter from '../content-hub/ContentHubNewsLetter';
import { SidebarData } from '../content-hub/ContentHubPageContainer';
import FontAwesomeIcon from '../icons/FontAwesomeIcon';
import { openToast } from '../toast/ToastContext';
import {
  SideBar,
  SideBarContainer,
  ArticleSectionContainer,
  ArticleSectionTitle,
  ArticleNavLink,
  ShareLink,
  ShareLinks,
  ArticleNavTitle,
} from './ArticleSideBar.style';

const FACEBOOK_LINK = (link: string) =>
  `https://www.facebook.com/dialog/feed?app_id=311631040612679&link=${encodeURIComponent(
    link,
  )}&redirect_uri=${encodeURIComponent(link)}%3Ffb%3Dtrue`;

const TWITTER_LINK = (link: string, title?: string) =>
  `https://x.com/intent/post?url=${encodeURIComponent(link)}&text=${encodeURIComponent(
    title || `Check out this article found on ${platform.title}`,
  )}`;

const EMAIL_LINK = (link: string, title?: string) =>
  `mailto:?subject=${encodeURIComponent(
    title || `Check out this article found on ${platform.title}`,
  )}&body=${encodeURIComponent(`Check out this article found on ${platform.title}:`)}%0D%0A${encodeURIComponent(link)}`;

const LINKEDIN_LINK = (link: string) =>
  `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(link)}`;

interface ArticleSideBarProps extends SidebarData {}

const ArticleSideBar: React.FC<ArticleSideBarProps> = ({
  // tag,
  nextArticle,
  prevArticle,
  category,
  subCategory,
  articleTitle,
  hideSubscription,
}) => {
  const router = useRouter();

  const shareURL = useMemo(() => `https://${platform.domain}${router.asPath}`, [router.asPath]);

  const trackClick = (media: string) => () => {
    trackGaEvent('share-article', {
      media,
    });
  };

  const splitByLastWord = (str: string): string[] => {
    const lastSpaceIndex = str.search(/\s(?!.*\s)/);

    if (lastSpaceIndex === -1) return [str];

    return [str.substring(0, lastSpaceIndex), ` ${str.substring(lastSpaceIndex + 1)}`];
  };

  const splitPreviousArticle = useMemo(() => {
    if (prevArticle?.attributes?.title) return splitByLastWord(prevArticle.attributes.title);
    return null;
  }, [prevArticle]);

  const splitNextArticle = useMemo(() => {
    if (nextArticle?.attributes?.title) return splitByLastWord(nextArticle.attributes.title);
    return null;
  }, [nextArticle]);

  const prevArticlePath = useMemo(() => {
    if (!category || !subCategory || !prevArticle?.attributes.slug) return null;
    const paths = platform.select(
      [category, subCategory, prevArticle.attributes.slug],
      [subCategory, prevArticle.attributes.slug],
    );

    return `/${paths.join('/')}`;
  }, [category, subCategory, prevArticle]);

  const nextArticlePath = useMemo(() => {
    if (!category || !subCategory || !nextArticle?.attributes.slug) return null;
    const paths = platform.select(
      [category, subCategory, nextArticle.attributes.slug],
      [subCategory, nextArticle.attributes.slug],
    );

    return `/${paths.join('/')}`;
  }, [category, subCategory, nextArticle]);

  return (
    <SideBar data-testid="article-side-bar">
      <SideBarContainer>
        {/* {tag && (
          <ArticleTag data-testid="article-tag-sidebar" colour={tag.textColour} bg={tag.backgroundColour} mb={30}>
            {tag.tag}
          </ArticleTag>
        )} */}
        <ArticleSectionContainer data-testid="article-shares" noBorder>
          <ArticleSectionTitle>Share this article</ArticleSectionTitle>
          <ShareLinks>
            <ShareLink
              href={`${FACEBOOK_LINK(shareURL)}`}
              target="_blank"
              onClick={trackClick('facebook')}
              data-testid="share-facebook"
            >
              <FontAwesomeIcon icon={faFacebookF} />
            </ShareLink>
            <ShareLink
              href={`${TWITTER_LINK(shareURL, articleTitle)}`}
              target="_blank"
              onClick={trackClick('twitter')}
              data-testid="share-twitter"
            >
              <FontAwesomeIcon icon={faXTwitter} />
            </ShareLink>
            <ShareLink
              href={`${LINKEDIN_LINK(shareURL)}`}
              target="_blank"
              onClick={trackClick('linkedin')}
              data-testid="share-linkedin"
            >
              <FontAwesomeIcon icon={faLinkedinIn} />
            </ShareLink>
            <ShareLink
              href={`${EMAIL_LINK(shareURL, articleTitle)}`}
              target="_blank"
              onClick={trackClick('email')}
              data-testid="share-email"
            >
              <FontAwesomeIcon icon={faEnvelope} />
            </ShareLink>
            <ShareLink
              as="button"
              onClick={() => {
                trackClick('copy-to-clipboard');
                navigator.clipboard.writeText(shareURL);
                openToast({
                  message: (
                    <>
                      Successfully copied <b>{articleTitle}</b> article <b>URL</b> to your clipboard!
                    </>
                  ),
                });
              }}
              data-testid="copy-to-clipboard"
            >
              <FontAwesomeIcon icon={faLink} />
            </ShareLink>
          </ShareLinks>
        </ArticleSectionContainer>
        {!hideSubscription && (
          <ArticleSectionContainer>
            <ContentHubNewsLetter
              sidebar
              page="Article Detail"
              location="Right hand pane"
              extraParams={{
                article: {
                  path: router.asPath,
                  title: articleTitle,
                },
              }}
            />
          </ArticleSectionContainer>
        )}
        {prevArticle && splitPreviousArticle && prevArticlePath && (
          <ArticleSectionContainer data-testid="previous-article">
            <ArticleSectionTitle>View previous article</ArticleSectionTitle>
            <ArticleNavLink prefetch={false} href={prevArticlePath}>
              {splitPreviousArticle.map((title, i) => (
                <ArticleNavTitle key={title} lastWord={i === splitPreviousArticle.length - 1}>
                  {title}
                </ArticleNavTitle>
              ))}
            </ArticleNavLink>
          </ArticleSectionContainer>
        )}
        {nextArticle && splitNextArticle && nextArticlePath && (
          <ArticleSectionContainer data-testid="next-article">
            <ArticleSectionTitle>View next article</ArticleSectionTitle>
            <ArticleNavLink prefetch={false} href={nextArticlePath}>
              {splitNextArticle.map((title, i) => (
                <ArticleNavTitle key={title} lastWord={i === splitNextArticle.length - 1}>
                  {title}
                </ArticleNavTitle>
              ))}
            </ArticleNavLink>
          </ArticleSectionContainer>
        )}
      </SideBarContainer>
    </SideBar>
  );
};

export default ArticleSideBar;
